/**
 * wayai migrate — relocate a legacy repo to the `wayai-ws/{hubs,org}` layout.
 *
 *   workspace/<hub>/  →  wayai-ws/hubs/<hub>/
 *   org/              →  wayai-ws/org/
 *
 * Idempotent: a no-op when there's nothing legacy left to move. Uses `git mv`
 * (preserving history) for tracked dirs, falling back to a filesystem rename for
 * untracked / partially-tracked dirs or non-git repos. `.wayai.yaml` and any
 * root-level agent-context files (e.g. a user's own `AGENTS.md`) stay at the
 * root and are untouched.
 */

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import { findGitRoot } from '../lib/workspace.js';
import { WAYAI_LAYOUT, isDirectory } from '../lib/layout.js';

/** True when at least one tracked file lives at/under `p`. */
function isTracked(gitRoot: string, p: string): boolean {
  try {
    execFileSync('git', ['ls-files', '--error-unmatch', '--', path.relative(gitRoot, p)], {
      cwd: gitRoot,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Move `from` → `to`, creating `to`'s parent. Prefers `git mv` for tracked
 * sources (history-preserving, staged); falls back to `fs.rename` when the
 * source is untracked or `git mv` errors (e.g. mixed tracked/untracked
 * contents). Returns which mechanism was used.
 */
function moveDir(gitRoot: string, from: string, to: string): 'git' | 'fs' {
  fs.mkdirSync(path.dirname(to), { recursive: true });

  if (isTracked(gitRoot, from)) {
    try {
      execFileSync('git', ['mv', path.relative(gitRoot, from), path.relative(gitRoot, to)], {
        cwd: gitRoot,
        stdio: ['pipe', 'pipe', 'pipe'],
      });
      return 'git';
    } catch {
      // Fall through — e.g. the dir mixes tracked and untracked children.
    }
  }

  fs.renameSync(from, to);
  return 'fs';
}

export async function migrateCommand(_args: string[]): Promise<void> {
  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not inside a git repository.');
    process.exit(1);
  }

  const rel = (p: string) => path.relative(gitRoot, p);

  const newWs = path.join(gitRoot, WAYAI_LAYOUT.wsDir);
  const legacyWs = path.join(gitRoot, WAYAI_LAYOUT.legacy.wsDir);
  const legacyOrg = path.join(gitRoot, WAYAI_LAYOUT.legacy.orgAtRoot);
  const newHubs = path.join(newWs, WAYAI_LAYOUT.hubsSubdir);
  const newOrg = path.join(newWs, WAYAI_LAYOUT.orgSubdir);

  const hasLegacyWs = isDirectory(legacyWs);
  const hasLegacyOrg = isDirectory(legacyOrg);

  if (!hasLegacyWs && !hasLegacyOrg) {
    console.log(`Already migrated — no legacy ${WAYAI_LAYOUT.legacy.wsDir}/ or ${WAYAI_LAYOUT.legacy.orgAtRoot}/ to move.`);
    return;
  }

  // Validate ALL targets before moving ANYTHING. Checking per-dir inline would
  // let the workspace move succeed and then abort on an org-target collision,
  // leaving a half-migrated repo that a re-run can't cleanly recover.
  if (hasLegacyWs && isDirectory(newHubs)) {
    console.error(
      `Cannot migrate: target ${rel(newHubs)}/ already exists alongside legacy ${WAYAI_LAYOUT.legacy.wsDir}/. ` +
        `Merge them manually, then re-run \`wayai migrate\`.`,
    );
    process.exit(1);
  }
  if (hasLegacyOrg && isDirectory(newOrg)) {
    console.error(
      `Cannot migrate: target ${rel(newOrg)}/ already exists alongside legacy ${WAYAI_LAYOUT.legacy.orgAtRoot}/. ` +
        `Merge them manually, then re-run \`wayai migrate\`.`,
    );
    process.exit(1);
  }

  const moved: string[] = [];
  let usedFsFallback = false;

  if (hasLegacyWs) {
    const how = moveDir(gitRoot, legacyWs, newHubs);
    usedFsFallback ||= how === 'fs';
    moved.push(`${WAYAI_LAYOUT.legacy.wsDir}/ → ${rel(newHubs)}/`);
  }

  if (hasLegacyOrg) {
    const how = moveDir(gitRoot, legacyOrg, newOrg);
    usedFsFallback ||= how === 'fs';
    moved.push(`${WAYAI_LAYOUT.legacy.orgAtRoot}/ → ${rel(newOrg)}/`);
  }

  console.log(`Migrated to the ${WAYAI_LAYOUT.wsDir}/ layout:`);
  for (const m of moved) console.log(`  ${m}`);
  if (usedFsFallback) {
    console.log('\n(Some dirs were moved on disk, not via git — they will show as untracked.)');
  }
  console.log('\nNext steps:');
  console.log('  git add -A && git commit -m "chore: migrate to wayai-ws layout"');
  console.log('  If your CI runs `wayai push`, commit this migration in the same change that updates CI.');
}
